import React, { useState, useEffect } from 'react';
import {
  TrendingUp,
  Edit3,
  Check,
  X,
  Building2,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Target,
  Trophy,
} from 'lucide-react';
import { Card } from '../../Components/ui';

const NOMES_MESES = [
  'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
  'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
];

const NOMES_COMPLETOS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

/**
 * Componente de Gráfico de Tendências de Inscritos (Barras com Linha)
 * Mostra os 12 meses do ano selecionado com visualização combinada (Barra + Linha de Tendência).
 * Inclui Linha de Meta editável com sinal visual de conquista e histórico prévio configurável.
 */
export default function GraficoTendenciaInscritos({
  dados,
  ano,
  onChangeAno,
  instituicoes = [],
  idInstituicaoSelecionada,
  onChangeInstituicao,
  onSalvarManual,
  onSalvarMeta,
  podeEditar = false,
  carregando = false,
}) {
  const [modalAberto, setModalAberto] = useState(false);
  const [modalMetaAberto, setModalMetaAberto] = useState(false);
  const [valoresForm, setValoresForm] = useState({});
  const [metaInput, setMetaInput] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const meses = dados?.data?.meses || dados?.meses || [];
  const manuaisExistentes = dados?.data?.manuais || dados?.manuais || {};
  const metaAtual = dados?.data?.meta !== undefined ? dados?.data?.meta : (dados?.meta || 0);

  // Sincroniza o input da meta quando a meta atual carregar
  useEffect(() => {
    setMetaInput(metaAtual > 0 ? String(metaAtual) : '');
  }, [metaAtual]);

  // Alunos do mês atual / último mês com dados
  const ultimoMesComDados = [...meses].filter((m) => m.origem !== 'futuro' && m.total > 0).pop();
  const totalAtual = ultimoMesComDados?.total || 0;
  const metaAlcancada = metaAtual > 0 && totalAtual >= metaAtual;

  // Calcula maior valor para dimensionar a altura das barras (considerando também a meta para caber no gráfico)
  const maxTotal = Math.max(10, metaAtual, ...meses.map((m) => m.total || 0));
  // Folga no topo para linha e meta não encostarem no topo
  const escalaY = Math.ceil(maxTotal * 1.18);

  const abrirModal = () => {
    // Inicializa o formulário com os valores manuais atuais ou vazios
    const inicial = {};
    for (let m = 1; m <= 12; m++) {
      inicial[m] = manuaisExistentes[m] !== undefined ? manuaisExistentes[m] : '';
    }
    setValoresForm(inicial);
    setModalAberto(true);
  };

  const handleSalvar = async (e) => {
    e.preventDefault();
    setSalvando(true);
    try {
      await onSalvarManual(valoresForm);
      setModalAberto(false);
    } finally {
      setSalvando(false);
    }
  };

  // Coordenadas para renderização SVG do gráfico
  const svgWidth = 840;
  const svgHeight = 220;
  const paddingBottom = 28;
  const paddingTop = 24;
  const chartHeight = svgHeight - paddingBottom - paddingTop;
  const colWidth = svgWidth / 12;
  const barWidth = Math.min(34, colWidth * 0.52);

  // Calcula pontos da linha de tendência apenas para meses válidos/concluídos (ou manuais)
  const pontosLinha = meses.map((item, index) => {
    const x = index * colWidth + colWidth / 2;
    const yRatio = escalaY > 0 ? (item.total || 0) / escalaY : 0;
    const y = paddingTop + chartHeight - yRatio * chartHeight;
    const isFuturo = item.origem === 'futuro' && !item.total;
    return { x, y, isFuturo, ...item };
  });

  // Linha traçada apenas pelos meses ativos/concluídos
  const pontosAtivosLinha = pontosLinha.filter((p) => !p.isFuturo);
  const linhaD = pontosAtivosLinha.reduce(
    (acc, p, i) => `${acc} ${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`,
    ''
  );

  return (
    <Card padding="none" className="overflow-hidden border border-gray-100 dark:border-gray-800 shadow-sm">
      {/* Cabeçalho do Card */}
      <div className="p-3.5 sm:p-4 border-b border-gray-100 dark:border-gray-800 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-white dark:bg-gray-900">
        <div className="flex items-center gap-2.5">
          <div className="p-2 rounded-xl bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400">
            <TrendingUp className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-1.5">
              Tendência Anual de Inscritos
              <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-blue-100/70 text-blue-700 dark:bg-blue-900/60 dark:text-blue-300">
                Barras + Linha
              </span>
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Evolução mensal com dados automáticos do sistema e histórico prévio configurável.
            </p>
          </div>
        </div>

        {/* Controles: Seletor de Ano, Instituição e Botão de Edição Manual */}
        <div className="flex items-center flex-wrap gap-2 w-full sm:w-auto justify-end">
          {/* Seletor de Instituição (se houver mais de uma vinculada) */}
          {instituicoes.length > 1 && (
            <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/80 px-2 py-1.5 rounded-lg border border-gray-200/80 dark:border-gray-700">
              <Building2 className="w-3.5 h-3.5 text-gray-400 shrink-0" />
              <select
                value={idInstituicaoSelecionada || ''}
                onChange={(e) => onChangeInstituicao(Number(e.target.value))}
                className="bg-transparent text-xs font-medium text-gray-800 dark:text-gray-200 outline-none cursor-pointer"
              >
                {instituicoes.map((inst) => (
                  <option key={inst.id} value={inst.id} className="dark:bg-gray-800">
                    {inst.nome}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Navegador de Ano */}
          <div className="flex items-center bg-gray-50 dark:bg-gray-800/80 rounded-lg border border-gray-200/80 dark:border-gray-700 p-0.5 text-xs font-medium">
            <button
              type="button"
              onClick={() => onChangeAno(ano - 1)}
              className="p-1 text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 transition-colors rounded hover:bg-gray-200/60 dark:hover:bg-gray-700"
              title="Ano anterior"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            <span className="px-2 font-semibold text-gray-700 dark:text-gray-200">{ano}</span>
            <button
              type="button"
              onClick={() => onChangeAno(ano + 1)}
              className="p-1 text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 transition-colors rounded hover:bg-gray-200/60 dark:hover:bg-gray-700"
              title="Próximo ano"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Botão de Histórico Manual */}
          {podeEditar && (
            <button
              type="button"
              onClick={abrirModal}
              className="flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg shadow-2xs transition-all border border-gray-200/80 dark:border-gray-700"
            >
              <Edit3 className="w-3.5 h-3.5" />
              Histórico Manual
            </button>
          )}

          {/* Botão de Linha de Meta */}
          {podeEditar && (
            <button
              type="button"
              onClick={() => setModalMetaAberto(true)}
              className="flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg shadow-sm transition-all"
            >
              <Target className="w-3.5 h-3.5" />
              {metaAtual > 0 ? `Meta: ${metaAtual}` : 'Definir Meta'}
            </button>
          )}
        </div>
      </div>

      {/* Banner de Celebração de Meta Alcançada (quando alcançada) */}
      {metaAlcancada && (
        <div className="px-4 py-2 bg-gradient-to-r from-emerald-500/15 via-teal-500/10 to-transparent dark:from-emerald-950/40 border-b border-emerald-200/60 dark:border-emerald-800/50 flex items-center justify-between text-xs text-emerald-800 dark:text-emerald-300">
          <div className="flex items-center gap-2">
            <span className="p-1 rounded-full bg-emerald-600 text-white shadow-xs">
              <Trophy className="w-3.5 h-3.5 animate-bounce" />
            </span>
            <span className="font-semibold">
              Meta Anual Conquistada!
            </span>
            <span className="text-[11px] text-emerald-700/80 dark:text-emerald-400">
              Esta instituição atingiu {totalAtual} inscritos, superando a meta de {metaAtual}! 🎉
            </span>
          </div>
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-600 text-white uppercase tracking-wider">
            100%+
          </span>
        </div>
      )}

      {/* Legenda Informativa */}
      <div className="px-4 py-2 bg-gray-50/70 dark:bg-gray-900/60 border-b border-gray-100 dark:border-gray-800 flex flex-wrap items-center justify-between text-[11px] text-gray-500 dark:text-gray-400 gap-2">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-gradient-to-t from-blue-600 to-blue-400 inline-block" />
            <span>Dados automáticos do sistema</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-gradient-to-t from-amber-600 to-amber-400 inline-block" />
            <span>Informado manualmente</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-4 h-0.5 bg-blue-700 dark:bg-blue-400 inline-block rounded-full" />
            <span>Curva de tendência</span>
          </div>
          {metaAtual > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="w-4 border-b-2 border-dashed border-emerald-500 inline-block" />
              <span className="text-emerald-700 dark:text-emerald-400 font-medium">Meta ({metaAtual})</span>
            </div>
          )}
        </div>
        <div className="text-[10px] text-gray-400 dark:text-gray-500 italic">
          Passe o mouse sobre as colunas para detalhes
        </div>
      </div>

      {/* Área do Gráfico */}
      <div className="p-4 bg-white dark:bg-gray-900 relative">
        {carregando ? (
          <div className="h-[220px] flex items-center justify-center text-xs text-gray-400 dark:text-gray-500">
            Carregando tendências...
          </div>
        ) : meses.length === 0 ? (
          <div className="h-[220px] flex items-center justify-center text-xs text-gray-400 dark:text-gray-500">
            Nenhum dado encontrado para o ano de {ano}.
          </div>
        ) : (
          <div className="w-full overflow-x-auto">
            <div className="min-w-[680px]">
              <svg
                viewBox={`0 0 ${svgWidth} ${svgHeight}`}
                className="w-full h-[220px] overflow-visible select-none"
              >
                <defs>
                  {/* Gradiente Barras Sistema */}
                  <linearGradient id="gradienteSistema" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.95" />
                    <stop offset="100%" stopColor="#1D4ED8" stopOpacity="0.75" />
                  </linearGradient>

                  {/* Gradiente Barras Manuais */}
                  <linearGradient id="gradienteManual" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#F59E0B" stopOpacity="0.95" />
                    <stop offset="100%" stopColor="#D97706" stopOpacity="0.75" />
                  </linearGradient>

                  {/* Sombra suave para a linha de tendência */}
                  <filter id="shadowLinha" x="-10%" y="-10%" width="120%" height="120%">
                    <feDropShadow dx="0" dy="2" stdDeviation="2.5" floodColor="#1E40AF" floodOpacity="0.25" />
                  </filter>
                </defs>

                {/* Linhas de Grade de Fundo (Horizontal) */}
                {[0, 0.25, 0.5, 0.75, 1].map((pct, idx) => {
                  const yVal = paddingTop + chartHeight * (1 - pct);
                  const labelVal = Math.round(escalaY * pct);
                  return (
                    <g key={idx} className="text-gray-300 dark:text-gray-800">
                      <line
                        x1={0}
                        y1={yVal}
                        x2={svgWidth}
                        y2={yVal}
                        stroke="currentColor"
                        strokeDasharray={pct === 0 ? 'none' : '3 3'}
                        strokeWidth="1"
                        opacity={pct === 0 ? '0.4' : '0.2'}
                      />
                      {idx > 0 && (
                        <text
                          x={6}
                          y={yVal - 3}
                          fontSize="9"
                          fill="#9CA3AF"
                          className="font-mono"
                        >
                          {labelVal}
                        </text>
                      )}
                    </g>
                  );
                })}

                {/* Barras Verticais de cada Mês */}
                {pontosLinha.map((p, i) => {
                  const yRatio = escalaY > 0 ? (p.total || 0) / escalaY : 0;
                  const bHeight = Math.max(3, yRatio * chartHeight);
                  const bX = p.x - barWidth / 2;
                  const bY = paddingTop + chartHeight - bHeight;
                  const isManual = p.origem === 'manual';
                  const isHovered = hoveredIndex === i;
                  const temDados = !p.isFuturo && p.total > 0;

                  return (
                    <g
                      key={`bar-${i}`}
                      onMouseEnter={() => (temDados || isManual) && setHoveredIndex(i)}
                      onMouseLeave={() => setHoveredIndex(null)}
                      className={`${temDados || isManual ? 'cursor-pointer' : 'cursor-default'} transition-all duration-200`}
                    >
                      {/* Barra com cantos superiores arredondados (só exibe se tiver dados e não for futuro vazio) */}
                      {temDados && (
                        <rect
                          x={bX}
                          y={bY}
                          width={barWidth}
                          height={bHeight}
                          rx="5"
                          ry="5"
                          fill={isManual ? 'url(#gradienteManual)' : 'url(#gradienteSistema)'}
                          opacity={isHovered ? 1 : 0.9}
                          className="transition-all duration-150"
                        />
                      )}

                      {/* Rótulo do valor no topo da barra */}
                      {temDados && (
                        <text
                          x={p.x}
                          y={bY - 6}
                          textAnchor="middle"
                          fontSize="10"
                          fontWeight="600"
                          fill={isManual ? '#D97706' : '#2563EB'}
                          className="font-sans"
                        >
                          {p.total}
                        </text>
                      )}

                      {/* Label do Mês no Rodapé */}
                      <text
                        x={p.x}
                        y={svgHeight - 8}
                        textAnchor="middle"
                        fontSize="11"
                        fontWeight={isHovered ? '700' : '500'}
                        fill={p.isFuturo ? '#D1D5DB' : isHovered ? '#1F2937' : '#6B7280'}
                        className={p.isFuturo ? 'dark:fill-gray-600' : 'dark:fill-gray-400'}
                      >
                        {NOMES_MESES[i]}
                      </text>
                    </g>
                  );
                })}

                {/* Linha Contínua de Tendência Sobreposta (apenas pelos meses com dados) */}
                {pontosAtivosLinha.length > 1 && (
                  <path
                    d={linhaD}
                    fill="none"
                    stroke="#1E40AF"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    filter="url(#shadowLinha)"
                    className="dark:stroke-blue-400"
                  />
                )}

                {/* Linha Tracejada de Meta (quando configurada) */}
                {metaAtual > 0 && (() => {
                  const yRatio = escalaY > 0 ? metaAtual / escalaY : 0;
                  const metaY = paddingTop + chartHeight - yRatio * chartHeight;
                  return (
                    <g className="cursor-pointer" onClick={() => podeEditar && setModalMetaAberto(true)}>
                      <line
                        x1={0}
                        y1={metaY}
                        x2={svgWidth}
                        y2={metaY}
                        stroke="#10B981"
                        strokeDasharray="6 4"
                        strokeWidth="2"
                        className="dark:stroke-emerald-400 opacity-80 hover:opacity-100 transition-opacity"
                      />
                      {/* Selo no fim da linha de meta */}
                      <rect
                        x={svgWidth - 75}
                        y={metaY - 10}
                        width={70}
                        height={18}
                        rx={4}
                        fill={metaAlcancada ? '#059669' : '#10B981'}
                        className="shadow-sm"
                      />
                      <text
                        x={svgWidth - 40}
                        y={metaY + 2.5}
                        textAnchor="middle"
                        fontSize="9"
                        fontWeight="700"
                        fill="#FFFFFF"
                      >
                        {metaAlcancada ? `🎯 ${metaAtual} ✓` : `META ${metaAtual}`}
                      </text>
                    </g>
                  );
                })()}

                {/* Pontos da Linha de Tendência (apenas para meses ativos) */}
                {pontosAtivosLinha.map((p, i) => {
                  const originalIndex = p.mes - 1;
                  const isHovered = hoveredIndex === originalIndex;
                  return (
                    <g
                      key={`dot-${i}`}
                      onMouseEnter={() => setHoveredIndex(originalIndex)}
                      onMouseLeave={() => setHoveredIndex(null)}
                      className="cursor-pointer"
                    >
                      <circle
                        cx={p.x}
                        cy={p.y}
                        r={isHovered ? 6 : 4}
                        fill="#FFFFFF"
                        stroke={p.origem === 'manual' ? '#D97706' : '#1D4ED8'}
                        strokeWidth="2.5"
                        className="transition-all duration-150"
                      />
                    </g>
                  );
                })}
              </svg>
            </div>
          </div>
        )}

        {/* Tooltip em Destaque ao Passar o Mouse */}
        {hoveredIndex !== null && pontosLinha[hoveredIndex] && (
          <div
            className="absolute z-20 pointer-events-none transform -translate-x-1/2 bg-gray-900/95 text-white text-xs px-3 py-2 rounded-xl shadow-xl backdrop-blur-sm border border-gray-700/60 flex flex-col gap-0.5"
            style={{
              left: `${(hoveredIndex * colWidth + colWidth / 2) / (svgWidth / 100)}%`,
              top: '12px',
            }}
          >
            <div className="font-semibold text-gray-200 flex items-center justify-between gap-3">
              <span>{NOMES_COMPLETOS[hoveredIndex]} / {ano}</span>
              <span
                className={`text-[9px] px-1.5 py-0.2 rounded font-bold uppercase tracking-wider ${
                  pontosLinha[hoveredIndex].origem === 'manual'
                    ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                    : 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                }`}
              >
                {pontosLinha[hoveredIndex].origem === 'manual' ? 'Manual' : 'Sistema'}
              </span>
            </div>
            <div className="text-lg font-bold text-white flex items-baseline gap-1">
              {pontosLinha[hoveredIndex].total}
              <span className="text-[10px] font-normal text-gray-400">inscritos</span>
            </div>
            {pontosLinha[hoveredIndex].origem === 'manual' ? (
              <span className="text-[10px] text-amber-300">
                Valor histórico cadastrado manualmente.
              </span>
            ) : (
              <span className="text-[10px] text-gray-400">
                Calculado a partir das matrículas e histórico de alunos ativos no período.
              </span>
            )}
          </div>
        )}
      </div>

      {/* Modal de Configuração de Histórico Manual */}
      {modalAberto && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs">
          <div className="bg-white dark:bg-gray-900 rounded-2xl max-w-lg w-full p-5 shadow-2xl border border-gray-100 dark:border-gray-800 animate-in fade-in zoom-in duration-150">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100 dark:border-gray-800">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-xl bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400">
                  <Edit3 className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Histórico Manual de Inscritos ({ano})
                  </h4>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400">
                    Insira a quantidade de alunos para meses anteriores à utilização do sistema.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setModalAberto(false)}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSalvar} className="mt-4 space-y-4">
              <div className="p-2.5 rounded-lg bg-amber-50/80 dark:bg-amber-950/30 border border-amber-200/60 dark:border-amber-800/50 text-[11px] text-amber-800 dark:text-amber-300 flex items-start gap-2">
                <Sparkles className="w-4 h-4 shrink-0 text-amber-600 dark:text-amber-400 mt-0.5" />
                <div>
                  <strong>Como funciona:</strong> Os meses preenchidos aqui sobrescrevem o gráfico com
                  o número digitado. <em>Para voltar a usar o cálculo automático do sistema em qualquer mês, deixe o campo em branco.</em>
                </div>
              </div>

              {/* Grade de 12 Meses */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 max-h-[300px] overflow-y-auto pr-1">
                {NOMES_COMPLETOS.map((nomeMes, idx) => {
                  const mesNum = idx + 1;
                  return (
                    <div
                      key={mesNum}
                      className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/40 flex flex-col gap-1"
                    >
                      <label className="text-[11px] font-semibold text-gray-700 dark:text-gray-300">
                        {nomeMes}
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        placeholder="Automático"
                        value={valoresForm[mesNum] ?? ''}
                        onChange={(e) =>
                          setValoresForm((prev) => ({
                            ...prev,
                            [mesNum]: e.target.value === '' ? '' : e.target.value,
                          }))
                        }
                        className="w-full text-xs px-2 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                  );
                })}
              </div>

              {/* Botões do Modal */}
              <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100 dark:border-gray-800">
                <button
                  type="button"
                  onClick={() => setModalAberto(false)}
                  disabled={salvando}
                  className="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={salvando}
                  className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg shadow-sm transition-all"
                >
                  <Check className="w-3.5 h-3.5" />
                  {salvando ? 'Salvando...' : 'Salvar Alterações'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Configuração de Meta Anual */}
      {modalMetaAberto && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs">
          <div className="bg-white dark:bg-gray-900 rounded-2xl max-w-sm w-full p-5 shadow-2xl border border-gray-100 dark:border-gray-800 animate-in fade-in zoom-in duration-150">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100 dark:border-gray-800">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400">
                  <Target className="w-4 h-4" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Meta de Inscritos ({ano})
                  </h4>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400">
                    Defina o objetivo anual da instituição
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setModalMetaAberto(false)}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form
              onSubmit={async (e) => {
                e.preventDefault();
                setSalvando(true);
                try {
                  await onSalvarMeta(metaInput === '' ? 0 : Number(metaInput));
                  setModalMetaAberto(false);
                } finally {
                  setSalvando(false);
                }
              }}
              className="mt-4 space-y-4"
            >
              <div className="p-2.5 rounded-lg bg-emerald-50/80 dark:bg-emerald-950/30 border border-emerald-200/60 dark:border-emerald-800/50 text-[11px] text-emerald-800 dark:text-emerald-300">
                Uma linha tracejada será exibida no gráfico. Quando o número de inscritos atingir essa meta, um sinal visual de conquista e uma notificação serão gerados!
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                  Quantidade Alvo de Inscritos
                </label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  placeholder="Ex: 500"
                  value={metaInput}
                  onChange={(e) => setMetaInput(e.target.value)}
                  className="w-full text-sm px-3 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  autoFocus
                />
                <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">
                  Deixe vazio ou 0 para desativar a meta.
                </p>
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100 dark:border-gray-800">
                <button
                  type="button"
                  onClick={() => setModalMetaAberto(false)}
                  disabled={salvando}
                  className="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={salvando}
                  className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg shadow-sm transition-all"
                >
                  <Check className="w-3.5 h-3.5" />
                  {salvando ? 'Salvando...' : 'Salvar Meta'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Card>
  );
}
